import type { Feature, FeatureCollection } from "geojson";
import { type ComponentType, type RefObject, useEffect, useMemo } from "react";

import { useLastDefined, usePresence } from "../../components/presence";
import { useUi } from "../../state/ui";
import { revealPaint, useFadeIn } from "../reveal";
import { MAP_EXIT_MS } from "../units/phaseTransition";
import { FRIEND_BLUE } from "../units/symbols";
import { useDeployment } from "../units/useDeployment";
import { type Bounds, type CorridorCheck, linePoints, type UserCorridor, validate } from "./corridorEdit";
import { helperDistance, type LonLat } from "./corridorFrame";
import { useGestureState } from "./gestureState";
import {
  buildHandles,
  drawOrder,
  focusOf,
  handleKey,
  type HandlePoint,
  metersPerPixel,
  minorOpacity,
  sameHandle,
} from "./handles";
import { measureEndPoints, useMeasureView } from "./measureView";
import { useEased, useLingering } from "./motion";
import { type ManoeuvreHandle, useManoeuvre } from "./useManoeuvre";
import { WidthTag } from "./WidthTag";

/** Poignées et repères du mode édition du fuseau (docs/14 §2.3), dessinés par le moteur de carte (sans DOM,
 *  donc sans effet de pression) au-dessus du fuseau et des unités :
 *  - sommets : rond blanc r 5,5 à contour bleu ami 2 px ; survol r 7 ; saisi r 8 avec halo blanc ; sélectionné
 *    contour 3 px et rond intérieur bleu ami. Les sommets « mineurs » (allègement de l'affichage) apparaissent en
 *    fondu à l'approche du pointeur (60 px) ;
 *  - poignées de milieu (segments de plus de 48 px à l'écran) : r 3,5, contour en tirets, opacité 0,55, 1 au
 *    survol ;
 *  - extrémités de LIMA (r 4,5 ; 6 ; 7) et, pour la LIMA sélectionnée, ses courbures (r 3,5 ; 5 ; 6) et les
 *    milieux de son tracé ;
 *  - extrémités des lignes mobiles de la ZO du temps affiché (docs/15 §4.3, `measureEnd`), dessinées comme celles
 *    des LIMA : les glisser incline la ligne ;
 *  - poignées de l'objet sélectionné (extrémités et courbures de la LIMA sélectionnée, extrémités de la ligne de la
 *    ZO dont la fiche est ouverte) et extrémité survolée ou saisie au-dessus des sommets, les autres extrémités en
 *    dessous : confondue avec un sommet, c'est la poignée de l'objet sélectionné qui se saisit, sinon la plus
 *    proche, et la poignée qui se saisit est celle que l'on voit au-dessus (`pickHandle`, `drawOrder`,
 *    handles.ts) ;
 *  - segments fautifs (`validate`) en tirets rouges, contour de l'emprise admise en gris fin tireté (ce n'est pas
 *    une mesure tactique : pas de couleur APP-6), trait du pointeur vers l'autre limite et puce du geste.
 *  Entrée en fondu (200 ms) ; à la sortie, couches gardées à opacité nulle pendant `MAP_EXIT_MS`. */

interface EngineParts {
  Source: ComponentType<any>;
  Layer: ComponentType<any>;
  Marker: ComponentType<any>;
  /** Carte (images des poignées de milieu). */
  mapRef: RefObject<{ getMap: () => unknown } | null>;
  /** Zoom courant : allègement des sommets et filtre des milieux à 48 px. */
  zoom: number;
}

export const MANOEUVRE_LAYER_PREFIX = "manoeuvre-";
const MID_IMAGE = "gx-manoeuvre-mid";
const FADE_MS = 200;
const WHITE = "#ffffff";
const FAULT_RED = "#c83e3e";
const BOUNDS_GREY = "#8a909c";
const EMPTY: FeatureCollection = { type: "FeatureCollection", features: [] };
/** Extrémités de LIMA et des lignes de la ZO : r 4,5 ; survol 6 ; saisie 7. */
const END_PAINT = {
  "circle-radius": ["case", ["get", "active"], 7, ["get", "hover"], 6, 4.5],
  "circle-color": WHITE,
  "circle-stroke-color": FRIEND_BLUE,
  "circle-stroke-width": 2,
};

const collection = (features: Feature[]): FeatureCollection => ({ type: "FeatureCollection", features });
const point = (at: LonLat, properties: Record<string, unknown>): Feature => ({
  type: "Feature",
  geometry: { type: "Point", coordinates: at },
  properties,
});
const line = (coordinates: LonLat[], properties: Record<string, unknown> = {}): Feature => ({
  type: "Feature",
  geometry: { type: "LineString", coordinates },
  properties,
});

/** Poignée de milieu : rond blanc à contour bleu ami en tirets (r 3,5 ; dessiné au double de la résolution). */
function midImage(): { width: number; height: number; data: Uint8ClampedArray } | null {
  if (typeof document === "undefined") return null;
  const size = 22;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, 7, 0, Math.PI * 2);
  ctx.fillStyle = WHITE;
  ctx.fill();
  ctx.setLineDash([3.2, 2.6]);
  ctx.lineWidth = 3;
  ctx.strokeStyle = FRIEND_BLUE;
  ctx.stroke();
  const image = ctx.getImageData(0, 0, size, size);
  return { width: size, height: size, data: image.data };
}

interface ImageHost {
  hasImage: (id: string) => boolean;
  addImage: (id: string, image: { width: number; height: number; data: Uint8ClampedArray }, options?: { pixelRatio?: number }) => void;
}

/** Segments en faute (tirets rouges) : segments de limite entre deux paires fautives, voisinage d'une paire
 *  isolée (virage), barre de la paire (largeur), tracé des LIMA en cause. */
export function faultSegments(c: UserCorridor, check: CorridorCheck): LonLat[][] {
  if (!check.rules.length || c.left.length < 2) return [];
  const out: LonLat[][] = [];
  const pairs = [...new Set(check.pairs)].filter((i) => i >= 0 && i < c.left.length).sort((a, b) => a - b);
  const set = new Set(pairs);
  for (const i of pairs) {
    const linked = set.has(i + 1);
    if (linked) for (const rail of [c.left, c.right]) out.push([rail[i]!, rail[i + 1]!]);
    if (!linked && !set.has(i - 1)) {
      const from = Math.max(0, i - 1);
      const to = Math.min(c.left.length - 1, i + 1);
      for (const rail of [c.left, c.right]) out.push(rail.slice(from, to + 1));
    }
    if (check.rules.includes("width")) out.push([c.left[i]!, c.right[i]!]);
  }
  for (const name of check.lines) if (c.lines[name]) out.push(linePoints(c, name));
  return out;
}

const flags = (handle: ManoeuvreHandle, state: { hover: ManoeuvreHandle | null; active: ManoeuvreHandle | null; selected: ManoeuvreHandle | null }) => ({
  key: handleKey(handle),
  hover: sameHandle(handle, state.hover),
  active: sameHandle(handle, state.active),
  selected: sameHandle(handle, state.selected),
});

export function ManoeuvreLayers({ Source, Layer, Marker, mapRef, zoom }: EngineParts) {
  const editing = useManoeuvre((s) => s.editing);
  const working = useManoeuvre((s) => s.working);
  const hover = useManoeuvre((s) => s.hover);
  const active = useManoeuvre((s) => s.active);
  const selected = useManoeuvre((s) => s.selected);
  const selectedLine = useManoeuvre((s) => s.selectedLine);
  const pointer = useGestureState((s) => s.pointer);
  const tag = useGestureState((s) => s.tag);
  const tether = useGestureState((s) => s.tether);
  const historyPreview = useGestureState((s) => s.historyPreview);
  const limaVisible = useUi((s) => s.controlVisible.lima !== false);
  // LIMA du fuseau automatique glissée (aperçu du serveur) : ses extrémités se retirent le temps du geste.
  const draggedLine = useUi((s) => s.linePreview?.name ?? null);
  // Ligne de la ZO dont la fiche est ouverte : ses extrémités passent au-dessus des sommets.
  const selection = useUi((s) => s.selection);
  const { data } = useDeployment();
  const phase = useUi((s) => s.phase);
  const corridor = data?.corridor ?? null;
  const model = corridor?.model ?? null;
  const enabled = editing && model !== null && !data?.centac;

  // Entrée : couches créées transparentes puis montées à leur opacité (transition du moteur, 200 ms) ; sortie :
  // opacité nulle, couches gardées le temps de `MAP_EXIT_MS`.
  const shown = useFadeIn(enabled ? "manoeuvre" : null);
  const mounted = useLingering(enabled, MAP_EXIT_MS);
  // Opacités pilotées par les données (sommets mineurs, milieux) : même fondu, par l'horloge de l'animation.
  const fade = useEased(shown ? 1 : 0, FADE_MS);

  const geometry: UserCorridor | null = working ?? historyPreview ?? model;
  const bounds = (corridor?.bounds ?? null) as Bounds | null;

  // Image des poignées de milieu, ajoutée une fois au style.
  useEffect(() => {
    if (!mounted) return;
    const map = mapRef.current?.getMap() as ImageHost | undefined;
    if (!map || map.hasImage(MID_IMAGE)) return;
    const image = midImage();
    if (image) map.addImage(MID_IMAGE, image, { pixelRatio: 2 });
  }, [mounted, mapRef]);

  // Bornes dures de la clé de gabarit (docs/15 §2.4) : mêmes segments en faute que le contrôle des gestes.
  const levelKey = data?.level_key ?? null;
  const check = useMemo(
    () => (geometry && enabled ? validate(geometry, bounds, geometry.level, { levelKey }) : null),
    [geometry, bounds, enabled, levelKey],
  );

  // Extrémités des lignes de la ZO telles qu'elles sont dessinées (aperçu d'un geste compris).
  const measures = useMeasureView(enabled ? data : null);
  const measureEnds = useMemo(() => (measures ? measureEndPoints(measures.measures.lines, phase) : []), [measures, phase]);

  const handles = useMemo(() => {
    if (!geometry || geometry.left.length < 2) return null;
    const lat = (geometry.left[0]![1] + geometry.right[geometry.right.length - 1]![1]) / 2;
    return buildHandles(geometry, {
      mpp: metersPerPixel(zoom, lat),
      selectedLine,
      emphasised: [hover, active, selected],
      lines: limaVisible,
      measureEnds,
    });
  }, [geometry, zoom, selectedLine, hover, active, selected, limaVisible, measureEnds]);

  // Collections séparées : seule celle des sommets mineurs suit le pointeur à chaque mouvement.
  const layers = useMemo(() => {
    const state = { hover, active, selected };
    if (!handles) return { major: EMPTY, ends: EMPTY, endsTop: EMPTY, bends: EMPTY, halo: EMPTY, dots: EMPTY };
    const all = [...handles.vertices, ...handles.limaEnds, ...handles.limaBends, ...handles.measureEnds];
    const ends = [...handles.limaEnds, ...handles.measureEnds].filter(
      (item) => item.handle.kind !== "limaEnd" || item.handle.line !== draggedLine,
    );
    // Extrémités de l'objet sélectionné (LIMA, ligne de la ZO dont la fiche est ouverte) : au-dessus des sommets,
    // comme elles l'emportent à la saisie sur un sommet confondu (`pickHandle`) ; l'extrémité survolée ou saisie
    // aussi (la saisie la retient même sous un sommet) ; les autres en dessous.
    const focus = focusOf(selectedLine, selection);
    const top = (item: HandlePoint) => drawOrder(item.handle, focus, [hover, active]) === 2;
    return {
      major: collection(handles.vertices.filter((item) => item.major).map((item) => point(item.at, flags(item.handle, state)))),
      ends: collection(ends.filter((item) => !top(item)).map((item) => point(item.at, flags(item.handle, state)))),
      endsTop: collection(ends.filter(top).map((item) => point(item.at, flags(item.handle, state)))),
      bends: collection(handles.limaBends.map((item) => point(item.at, flags(item.handle, state)))),
      halo: collection(all.filter((item) => sameHandle(item.handle, active)).map((item) => point(item.at, {}))),
      dots: collection(
        all
          .filter((item) => sameHandle(item.handle, selected) && item.handle.kind !== "limaEnd" && item.handle.kind !== "measureEnd")
          .map((item) => point(item.at, {})),
      ),
    };
  }, [handles, hover, active, selected, draggedLine, selectedLine, selection]);

  const mids = useMemo(() => {
    if (!handles) return EMPTY;
    const state = { hover, active, selected };
    const withOpacity = (list: HandlePoint[]) =>
      list.map((item) => {
        const f = flags(item.handle, state);
        return point(item.at, { ...f, o: (f.hover || f.active ? 1 : 0.55) * fade });
      });
    return collection([...withOpacity(handles.mids), ...withOpacity(handles.limaMids)]);
  }, [handles, hover, active, selected, fade]);

  const minor = useMemo(() => {
    if (!handles || !pointer) return EMPTY;
    const state = { hover, active, selected };
    const features: Feature[] = [];
    for (const item of handles.vertices) {
      if (item.major) continue;
      const distance = helperDistance(pointer, item.at) / metersPerPixel(zoom, item.at[1]);
      const o = minorOpacity(distance) * fade;
      if (o > 0.001) features.push(point(item.at, { ...flags(item.handle, state), o }));
    }
    return collection(features);
  }, [handles, hover, active, selected, pointer, zoom, fade]);

  const outline = useMemo(() => {
    if (!bounds) return EMPTY;
    const [w, s, e, n] = bounds;
    return collection([line([[w, s], [e, s], [e, n], [w, n], [w, s]])]);
  }, [bounds]);

  const faults = useMemo(
    () => (geometry && check ? collection(faultSegments(geometry, check).map((coords) => line(coords))) : EMPTY),
    [geometry, check],
  );
  const tetherData = useMemo(() => (tether ? collection([line(tether)]) : EMPTY), [tether]);

  // Puce du geste : apparition en léger zoom, sortie de même (la dernière reste affichée pendant la sortie).
  const presence = usePresence(Boolean(tag && enabled));
  const lastTag = useLastDefined(tag);

  if (!mounted) return null;
  const on = shown && enabled;
  return (
    <>
      <Source id="gx-manoeuvre-bounds" type="geojson" data={outline}>
        <Layer
          id={`${MANOEUVRE_LAYER_PREFIX}bounds`}
          type="line"
          paint={revealPaint(
            "line",
            { "line-color": BOUNDS_GREY, "line-width": 1, "line-dasharray": [4, 3], "line-opacity": 0.9 },
            on,
            FADE_MS,
          )}
        />
      </Source>
      <Source id="gx-manoeuvre-faults" type="geojson" data={faults}>
        <Layer
          id={`${MANOEUVRE_LAYER_PREFIX}faults`}
          type="line"
          layout={{ "line-cap": "round" }}
          paint={revealPaint(
            "line",
            { "line-color": FAULT_RED, "line-width": 3.5, "line-dasharray": [1.4, 1.2] },
            on,
            FADE_MS,
          )}
        />
      </Source>
      <Source id="gx-manoeuvre-tether" type="geojson" data={tetherData}>
        <Layer
          id={`${MANOEUVRE_LAYER_PREFIX}tether`}
          type="line"
          paint={revealPaint(
            "line",
            { "line-color": FRIEND_BLUE, "line-width": 1.5, "line-dasharray": [2, 2] },
            on,
            FADE_MS,
          )}
        />
      </Source>
      <Source id="gx-manoeuvre-mids" type="geojson" data={mids}>
        <Layer
          id={`${MANOEUVRE_LAYER_PREFIX}mids`}
          type="symbol"
          layout={{ "icon-image": MID_IMAGE, "icon-allow-overlap": true, "icon-ignore-placement": true }}
          paint={{ "icon-opacity": ["get", "o"] }}
        />
      </Source>
      <Source id="gx-manoeuvre-halo" type="geojson" data={layers.halo}>
        <Layer
          id={`${MANOEUVRE_LAYER_PREFIX}halo`}
          type="circle"
          paint={revealPaint(
            "circle",
            { "circle-radius": 13, "circle-color": WHITE, "circle-opacity": 0.85, "circle-blur": 0.35, "circle-stroke-width": 0 },
            on,
            FADE_MS,
          )}
        />
      </Source>
      <Source id="gx-manoeuvre-ends" type="geojson" data={layers.ends}>
        <Layer id={`${MANOEUVRE_LAYER_PREFIX}lima-ends`} type="circle" paint={revealPaint("circle", END_PAINT, on, FADE_MS)} />
      </Source>
      <Source id="gx-manoeuvre-minor" type="geojson" data={minor}>
        <Layer
          id={`${MANOEUVRE_LAYER_PREFIX}vertices-minor`}
          type="circle"
          paint={{
            "circle-radius": 5.5,
            "circle-color": WHITE,
            "circle-stroke-color": FRIEND_BLUE,
            "circle-stroke-width": 2,
            "circle-opacity": ["get", "o"],
            "circle-stroke-opacity": ["get", "o"],
          }}
        />
      </Source>
      <Source id="gx-manoeuvre-vertices" type="geojson" data={layers.major}>
        <Layer
          id={`${MANOEUVRE_LAYER_PREFIX}vertices`}
          type="circle"
          paint={revealPaint(
            "circle",
            {
              "circle-radius": ["case", ["get", "active"], 8, ["get", "hover"], 7, 5.5],
              "circle-color": WHITE,
              "circle-stroke-color": FRIEND_BLUE,
              "circle-stroke-width": ["case", ["get", "selected"], 3, 2],
            },
            on,
            FADE_MS,
          )}
        />
      </Source>
      <Source id="gx-manoeuvre-bends" type="geojson" data={layers.bends}>
        <Layer
          id={`${MANOEUVRE_LAYER_PREFIX}lima-bends`}
          type="circle"
          paint={revealPaint(
            "circle",
            {
              "circle-radius": ["case", ["get", "active"], 6, ["get", "hover"], 5, 3.5],
              "circle-color": WHITE,
              "circle-stroke-color": FRIEND_BLUE,
              "circle-stroke-width": ["case", ["get", "selected"], 3, 2],
            },
            on,
            FADE_MS,
          )}
        />
      </Source>
      <Source id="gx-manoeuvre-ends-top" type="geojson" data={layers.endsTop}>
        <Layer
          id={`${MANOEUVRE_LAYER_PREFIX}ends-top`}
          type="circle"
          paint={revealPaint("circle", END_PAINT, on, FADE_MS)}
        />
      </Source>
      <Source id="gx-manoeuvre-dots" type="geojson" data={layers.dots}>
        <Layer
          id={`${MANOEUVRE_LAYER_PREFIX}selected-dot`}
          type="circle"
          paint={revealPaint("circle", { "circle-radius": 2.25, "circle-color": FRIEND_BLUE, "circle-stroke-width": 0 }, on, FADE_MS)}
        />
      </Source>
      {presence.mounted && lastTag && (
        <Marker
          longitude={lastTag.at[0]}
          latitude={lastTag.at[1]}
          anchor={lastTag.kind === "rule" ? "bottom" : "center"}
          offset={lastTag.kind === "rule" ? [0, -14] : [0, 0]}
          style={{ pointerEvents: "none" }}
        >
          <WidthTag tag={lastTag} closing={presence.closing} />
        </Marker>
      )}
    </>
  );
}
